import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';
import { ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { errorDetailLines } from '../../lib/error-details';
import { isStatePill } from '../../lib/pill';

// --- Button ------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover border border-transparent',
  secondary: 'bg-surface-2 text-ink border border-line hover:bg-surface-3',
  ghost: 'bg-transparent text-muted border border-transparent hover:bg-surface-2 hover:text-ink',
  danger: 'bg-transparent text-danger border border-danger/40 hover:bg-danger-soft',
  success: 'bg-transparent text-success border border-success/40 hover:bg-success-soft',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
}

// --- Card --------------------------------------------------------------------

export function Card({
  title,
  action,
  className,
  bodyClassName,
  children,
  embedded,
}: {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
  /**
   * Rendered inside something that already supplies the frame and header — a
   * Collapsible panel on the ONU page. Drops this card's own section chrome and
   * title bar so the two do not stack into a doubled header; the caller owns the
   * one header.
   */
  embedded?: boolean;
}) {
  if (embedded) return <div className={bodyClassName}>{children}</div>;
  return (
    <section className={clsx('card overflow-hidden', className)}>
      {(title || action) && (
        <header
          className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5"
          style={{
            background: 'color-mix(in srgb, var(--section-accent, #3b82f6) 12%, transparent)',
          }}
        >
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {action}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

// --- Stat card ---------------------------------------------------------------

export function StatCard({
  label,
  value,
  icon,
  tone = 'default',
  hint,
  onClick,
  active,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warn' | 'danger' | 'info';
  /** A line under the value. A node, so a breakdown can colour its segments. */
  hint?: ReactNode;
  /** Makes the card a filter control: clicking it narrows the list below. */
  onClick?: () => void;
  /** Drawn as chosen, so it is obvious which filter is applied. */
  active?: boolean;
}) {
  const toneClass = {
    default: 'text-ink',
    success: 'text-success',
    warn: 'text-warn',
    danger: 'text-danger',
    info: 'text-info',
  }[tone];

  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={clsx(
        'card px-5 py-4',
        onClick && 'w-full cursor-pointer text-left transition-colors hover:bg-surface-2',
        active && 'ring-1 ring-[var(--section-accent,var(--primary))]',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="text-sm text-muted">{label}</span>
        {icon && <span className="text-faint">{icon}</span>}
      </div>
      <div className={clsx('mt-3 text-3xl font-semibold tabular-nums', toneClass)}>{value}</div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </Tag>
  );
}

// --- Pill --------------------------------------------------------------------

export type PillTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info' | 'primary';

const PILL_TONES: Record<PillTone, string> = {
  neutral: 'bg-surface-2 text-muted border-line',
  success: 'bg-success-soft text-success border-success/30',
  warn: 'bg-warn-soft text-warn border-warn/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
  info: 'bg-info-soft text-info border-info/30',
  primary: 'bg-primary-soft text-primary border-primary/30',
};

/**
 * Two kinds of pill (operator, 2026-10-10: "standardize the pill colors across
 * the platform, all first pills should like a, all seconds ones like b, all
 * third ones like c").
 *
 * A LABEL pill (what something is: Internet, DHCP, Bridge, a model, a mode)
 * takes its colour from its POSITION among the pills beside it: the first is
 * blue, the second violet, the third cyan, the rest grey (`.pill-pos` in
 * index.css). A STATE pill (whether it is well: Connected, Offline, Firewall
 * off, Failed) keeps its tone, so a fault still stands out (the operator's
 * choice). A pill is a state when it says so (`state`), wears a dot, or is
 * green, amber or red; a pill whose tone is chosen from data at run time is
 * marked `state` where it is drawn, because its grey means "off", not "a
 * label". A lookup that colours CATEGORIES with state tones (a VLAN's purpose,
 * a backup's kind) passes `state={false}` and follows position.
 */
export function Pill({
  tone = 'neutral',
  dot = false,
  state,
  children,
  className,
  title,
}: {
  tone?: PillTone;
  dot?: boolean;
  /** A state pill keeps its tone; a label pill (false) takes its position's colour. Default: dot, or green/amber/red. */
  state?: boolean;
  children: ReactNode;
  className?: string;
  /** The longer sentence a short pill stands for, shown on hover. */
  title?: string;
}) {
  const isState = isStatePill({ tone, dot, state });
  return (
    <span
      title={title}
      className={clsx(
        'pill inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        isState ? PILL_TONES[tone] : 'pill-pos',
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

// --- Inputs ------------------------------------------------------------------

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx('input', className)} {...rest} />;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <span className="label">{label}</span>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-faint">{hint}</p>
      ) : null}
    </div>
  );
}


// --- Searchable select -------------------------------------------------------

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Second line, for the detail that makes two similar names distinguishable. */
  hint?: string | null;
}

/**
 * A select that can be typed into.
 *
 * A native `<select>` is fine for four VLANs and useless for four hundred
 * splitters: the operator knows the name and has to scroll for it. This keeps
 * the same shape and adds a filter, and only when there is something to filter
 * — below the visible count the search box would be furniture.
 *
 * Five rows visible by default, which is enough to see that a list continues
 * without the popover covering the form underneath it.
 */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  visibleRows = 5,
  emptyLabel,
  className,
  title,
  onCreate,
  createLabel,
}: {
  value: string;
  options: SearchSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** How many rows fit before the list scrolls. */
  visibleRows?: number;
  /** Offered as the "nothing selected" row; omitted when the field is required. */
  emptyLabel?: string;
  className?: string;
  /** Hover text on the trigger, for a filter whose name does not say enough. */
  title?: string;
  /**
   * Make a new one from here, instead of leaving to go and make it.
   *
   * Given the text the operator had typed, so the dialog opens with the name
   * they were already searching for — the common case is typing a zone that
   * does not exist yet and discovering that mid-search.
   *
   * Omitted when the caller cannot create: the row is an ACTION and offering
   * one that will be refused is worse than not offering it. Every call site
   * gates this on the capability rather than on the role.
   */
  onCreate?: (typed: string) => void;
  /** What the action row says when nothing has been typed. e.g. "New zone…". */
  createLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  /**
   * Where the open list is drawn, in viewport coordinates.
   *
   * The list is rendered through a PORTAL rather than as a child of this
   * component, because `Card` is `overflow-hidden` — it has to be, to clip a
   * table's corners against the rounded border — and an ancestor with
   * `overflow-hidden` clips an absolutely-positioned child no matter what its
   * z-index says. A timezone picker near the bottom of a card lost its options
   * into the card edge. Portalling to the body escapes every ancestor's
   * overflow at once, which is the only fix that does not depend on where the
   * select happens to be used.
   */
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;
  const searchable = options.length > visibleRows;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = emptyLabel ? [{ value: '', label: emptyLabel }, ...options] : options;
    if (!needle) return rows;
    return rows.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.hint ?? '').toLowerCase().includes(needle),
    );
  }, [options, query, emptyLabel]);

  /**
   * Height is ESTIMATED from the row count rather than measured, so the first
   * paint is already in the right place — a measure-then-move would show the
   * list jumping. Rows are 2rem, the list has 0.25rem of padding at each end,
   * the search box is 2.75rem when present, and there is a 1px border.
   */
  const place = useCallback(() => {
    const el = trigger.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rows = Math.min(Math.max(matches.length, 1), visibleRows);
    // The create row is pinned below the list rather than scrolling with it,
    // so it is height the popup needs on top of `visibleRows`.
    const wanted = rows * 32 + 8 + (searchable ? 44 : 0) + (onCreate ? 33 : 0) + 2;
    const gap = 4;
    const below = window.innerHeight - r.bottom - gap;
    const above = r.top - gap;
    // Below unless it genuinely does not fit and there is more room above.
    const dropUp = wanted > below && above > below;
    setPos({
      left: r.left,
      width: r.width,
      top: dropUp ? Math.max(gap, r.top - gap - Math.min(wanted, above)) : r.bottom + gap,
    });
  }, [matches.length, searchable, visibleRows, onCreate]);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  /**
   * A fixed-position list does not travel with the page, so it is re-placed on
   * scroll and resize. `capture` matters: the select may sit inside a scrolling
   * panel whose scroll never reaches the window.
   */
  useEffect(() => {
    if (!open) return;
    const again = () => place();
    window.addEventListener('scroll', again, true);
    window.addEventListener('resize', again);
    return () => {
      window.removeEventListener('scroll', again, true);
      window.removeEventListener('resize', again);
    };
  }, [open, place]);

  // Clicking anywhere else closes it, which is what every other select does.
  // The list lives outside this subtree now, so it is checked separately.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (root.current?.contains(target) || popup.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  useEffect(() => {
    if (open && searchable) search.current?.focus();
  }, [open, searchable]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
    setQuery('');
    setActive(0);
  }

  /**
   * Hand the typed text to the caller and get out of the way.
   *
   * The query is NOT cleared before the handover — it is the argument. It is
   * cleared after, so reopening the list is not still filtered by a name that
   * now exists and is selected.
   */
  function startCreate() {
    const typed = query.trim();
    setOpen(false);
    setActive(0);
    onCreate?.(typed);
    setQuery('');
  }

  function onKeyDown(e: ReactKeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        // One past the last option is the create row, when there is one —
        // reachable by keyboard like everything else, and never when there is
        // nothing to reach.
        const last = matches.length - 1 + (onCreate ? 1 : 0);
        return Math.max(0, Math.min(last, next));
      });
      return;
    }
    if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (onCreate && active === matches.length) {
        startCreate();
        return;
      }
      const row = matches[active];
      if (row) choose(row.value);
    }
  }

  return (
    <div ref={root} className={clsx('relative', className)} onKeyDown={onKeyDown}>
      <button
        ref={trigger}
        type="button"
        className="input flex w-full items-center justify-between gap-2 text-left"
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {/*
          Faint only when nothing is chosen AND choosing is required. With an
          `emptyLabel` the empty value is a real choice — "Any model" beside
          "Any signal" and "Any mode" on the same filter bar — so rendering it
          grey made one control look disabled next to two that were not.
        */}
        <span className={clsx('truncate', !selected && !emptyLabel && 'text-faint')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-faint" />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={popup}
              // `fixed`, and positioned from the trigger's own rect — see `pos`.
              className="fixed z-50 overflow-hidden rounded-lg border border-line bg-surface shadow-xl"
              /*
                The list is exactly as wide as the control it belongs to. A
                popup wider than its trigger reads as a different element that
                happened to appear there, which is what a 288px minimum looked
                like under a 176px filter. Rows carry a count and sometimes an
                "also stored as" note; those truncate, with the full text on
                the row's title, and the name keeps the space.
              */
              style={{ left: pos.left, top: pos.top, width: pos.width }}
              onKeyDown={onKeyDown}
            >
          {searchable && (
            <div className="border-b border-line p-2">
              <input
                ref={search}
                className="input h-8 text-xs"
                value={query}
                placeholder="Search…"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
            </div>
          )}
          <ul
            role="listbox"
            className="overflow-y-auto py-1"
            // Rows are 2rem and the list has 0.25rem of padding at each end, so
            // this shows exactly `visibleRows` and cuts the next one off
            // mid-row — which is what tells the reader the list continues.
            style={{ maxHeight: `${visibleRows * 2 + 0.5}rem` }}
          >
            {matches.length === 0 && (
              <li className="px-3 py-2 text-xs text-faint">
                {onCreate && query.trim()
                  ? `No match for “${query.trim()}”.`
                  : `Nothing matches “${query}”.`}
              </li>
            )}
            {matches.map((o, i) => (
              <li key={o.value || '__none'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  className={clsx(
                    'flex h-8 w-full items-center justify-between gap-2 px-3 text-left text-sm',
                    i === active ? 'bg-surface-2' : 'hover:bg-surface-2',
                    o.value === value && 'text-primary',
                  )}
                  title={o.hint ? `${o.label} — ${o.hint}` : o.label}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o.value)}
                >
                  {/* The name keeps the room; the hint gives way first. */}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.hint ? (
                    <span className="max-w-[45%] truncate text-xs text-faint">{o.hint}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          {/*
            The create row, pinned BELOW the list rather than appended to it.

            Three things follow from that placement and each is deliberate. It
            is always reachable, so an operator who has filtered the list down
            to nothing can still act — which is the whole case this exists for:
            you type a zone's name, discover it does not exist, and make it
            without leaving the form. It cannot scroll out of sight. And it is
            never one of `matches`, so it can never be chosen by a filter that
            happens to match its wording, and it can never be mistaken for a
            real object — the picker still names only things that exist, which
            is the rule the rest of these lists follow.

            When something has been typed, the row quotes it back: the operator
            is not choosing an abstract action, they are creating the thing they
            just searched for, and the dialog opens with that name in it.
          */}
          {onCreate ? (
            <div className="border-t border-line p-1">
              <button
                type="button"
                className={clsx(
                  'flex h-8 w-full items-center gap-2 rounded px-2 text-left text-sm text-link',
                  active === matches.length ? 'bg-surface-2' : 'hover:bg-surface-2',
                )}
                onMouseEnter={() => setActive(matches.length)}
                onClick={startCreate}
              >
                <Plus className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {query.trim()
                    ? `Create “${query.trim()}”`
                    : (createLabel ?? 'Create a new one…')}
                </span>
              </button>
            </div>
          ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// --- Table -------------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={clsx('w-full border-collapse', className)}>{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="border-b border-line bg-surface-2/60">
      <tr>{children}</tr>
    </thead>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line">{children}</tbody>;
}

export function TRow({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      className={clsx(
        'transition-colors hover:bg-surface-2/70',
        onClick && 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary',
        className,
      )}
      onClick={onClick}
      /**
       * A clickable row is reachable by keyboard too.
       *
       * `tabIndex` and Enter/Space rather than `role="button"`: the row is
       * still a row, and telling a screen reader otherwise would take away the
       * column headers that make the cells mean anything.
       */
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              // Space scrolls the page, and a row is not the thing that should.
              e.preventDefault();
              onClick();
            }
          : undefined
      }
    >
      {children}
    </tr>
  );
}

// --- Feedback ----------------------------------------------------------------

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      {icon && <div className="mb-1 text-faint">{icon}</div>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('size-4 animate-spin text-muted', className)} />;
}

export function ErrorNotice({ error }: { error: unknown }) {
  /*
    A string is a real, common input here (§8.5): thirteen callers already
    unwrapped `err.message` in their onError and hold it as `useState<string>`.
    `instanceof Error` is false for a string, so those all rendered the generic
    "Something went wrong" and threw away the server's actual sentence — the
    chassis's own words on a refused disable, the "between min and max" tunable
    refusals, every error in the migration dialog. The prop is `unknown`, which
    is exactly why the mistake compiled; handle the string case here, once.
  */
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : 'Something went wrong';
  /*
    Shown BESIDE the headline, never instead of it: "Request validation failed"
    is what the server said, and the lines under it are why.
  */
  const lines = errorDetailLines(error).filter((line) => line !== message);
  return (
    <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
      {message}
      {lines.length > 0 ? (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-ink">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
